import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Diamond, Check } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Motion";
import { DiagnosticInauguralWidget } from "@/components/DiagnosticInauguralWidget";
import { OFFERS } from "@/data/offers";

const CHAPTERS = [
  { n: "01", title: "IFÀ — La Mémoire", text: "Décrypter la parole ancestrale — Patrimoine immatériel de l'UNESCO — pour révéler la trame invisible de votre lignée et dénouer les dettes héritées." },
  { n: "02", title: "Lo Shu — L'Espace", text: "Harmoniser votre environnement par la grille magique et le Feng Shui Tibétain afin que chaque lieu serve votre ascension." },
  { n: "03", title: "Pythagore — Le Nombre", text: "Traduire la vibration de votre nom et de votre date en une architecture de décisions justes et souveraines." },
];

const HERO_IMG = "https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&q=85";
const FOUNDER_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/wingman/1d54338d-54b8-44b0-9db7-4c0dae9f7fd9/attachments/a14afeff05e64cc9be0dd034fde57cff_rodrigue_photo.jpg";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 via-brand-ink/70 to-brand-ink" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-28">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-xs md:text-sm uppercase tracking-luxe text-gold mb-8"
          >
            Académie AETHERIS — Architecture de Vie
          </motion.p>

          <h1 className="font-serif font-light text-white leading-[0.95] text-5xl md:text-7xl lg:text-8xl max-w-5xl">
            <MaskLines
              lines={["La Clarté au service", "de votre Souveraineté."]}
              start={0.3}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1 }}
            className="mt-10 text-lg md:text-xl text-white/60 font-light max-w-2xl leading-relaxed"
          >
            Ne subissez plus votre destin. Accédez à la justesse millénaire de la fusion
            IFÀ, Lo Shu et Pythagore pour lever vos blocages et bâtir votre propre empire.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/diagnostic-inaugural"
              data-testid="hero-cta-diagnostic"
              className="group bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-9 py-5 text-sm uppercase tracking-[0.15em] font-semibold inline-flex items-center gap-2"
            >
              Obtenir mon Diagnostic Inaugural — Gratuit
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="border border-white/25 text-white hover:border-gold hover:text-gold transition-colors px-9 py-5 text-sm uppercase tracking-[0.15em] inline-flex items-center gap-2"
            >
              Découvrir la Méthode
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 border-y border-white/10 py-6 bg-brand-surface">
        <Marquee speed={40} gradient={false} aria-label="Concepts clés de l'Académie AETHERIS">
          {["Triple Fusion™", "Numérologie Vibratoire", "Feng Shui Tibétain", "Architecture de Vie", "Grille Lo Shu", "Souveraineté Totale", "Dettes Karmiques"].map((w) => (
            <span key={w} className="mx-8 inline-flex items-center gap-8 font-serif text-2xl md:text-3xl text-white/80">
              {w}
              <Diamond size={12} className="text-gold" fill="currentColor" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-luxe text-gold mb-6">La Méthode Triple Fusion™</p>
          <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Trois savoirs millénaires, une seule trajectoire de puissance.
          </h2>
        </Reveal>

        {/* Formule signature Triple Fusion™ */}
        <Reveal delay={0.1} className="max-w-4xl mb-16">
          <blockquote
            className="relative pl-6 md:pl-10 py-6 border-l-2"
            style={{ borderColor: "#C9A96E" }}
            data-testid="triple-fusion-formula"
          >
            <p className="font-serif italic text-xl md:text-3xl leading-[1.35] text-white/90">
              <span className="text-gold not-italic font-normal">PYTHAGORE</span> décryptait les nombres.
              <span className="text-gold not-italic font-normal"> Lo Shu</span> décryptait l'espace.
              <span className="text-gold not-italic font-normal"> IFÀ</span> — Patrimoine immatériel de l'UNESCO — décryptait la destinée.
              <br className="hidden md:block" />
              <span className="text-white"> La Triple Fusion™ est la première méthode à réunir ces trois sagesses.</span>
            </p>
          </blockquote>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className="bg-brand-ink p-8 md:p-12 group">
              <span className="font-serif text-6xl text-gold/30 group-hover:text-gold transition-colors">{c.n}</span>
              <h3 className="font-serif text-2xl md:text-3xl text-white mt-6 mb-4">{c.title}</h3>
              <p className="text-white/50 font-light leading-relaxed">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DIAGNOSTIC INAUGURAL — widget interactif */}
      <section className="relative z-10 py-20 md:py-28" data-testid="home-diagnostic-inaugural">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal>
            <DiagnosticInauguralWidget compact />
            <p className="mt-5 text-xs text-white/40">
              <Link to="/diagnostic-inaugural" className="underline underline-offset-4 hover:text-gold transition-colors" data-testid="home-di-link">
                Ouvrir le Diagnostic Inaugural en pleine page
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* OFFERS */}
      <section className="relative z-10 bg-brand-surface border-y border-white/10 py-24 md:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-luxe text-gold mb-6">Les Parcours</p>
              <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight">
                Choisissez le degré de votre métamorphose.
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {OFFERS.map((o, i) => (
              <Reveal key={o.slug} delay={i * 0.08}>
                <div
                  data-testid={`offer-card-${o.slug}`}
                  className={`h-full flex flex-col p-8 border transition-colors duration-500 ${
                    o.featured ? "border-gold bg-brand-elevated" : "border-white/10 bg-brand-ink hover:border-gold/50"
                  }`}
                >
                  {o.featured && (
                    <span className="self-start text-[10px] uppercase tracking-luxe text-brand-ink bg-gold px-3 py-1 mb-4">
                      Le plus choisi
                    </span>
                  )}
                  <h3 className="font-serif text-xl text-white leading-tight">{o.name}</h3>
                  <p className="text-xs uppercase tracking-[0.15em] text-white/40 mt-2">{o.note}</p>
                  <div className="mt-6 flex items-baseline gap-3">
                    <span className="font-serif text-3xl text-gold">{o.price}</span>
                    {o.old && <span className="text-white/30 line-through text-lg">{o.old}</span>}
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {o.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-white/60">
                        <Check size={15} className="text-gold shrink-0 mt-0.5" strokeWidth={2} />
                        {p}
                      </li>
                    ))}
                  </ul>
                  {o.href ? (
                    <a href={o.href} target="_blank" rel="noopener noreferrer" data-testid={`offer-cta-${o.slug}`}
                      className={`mt-8 text-center px-5 py-4 text-[11px] uppercase tracking-[0.12em] font-semibold transition-colors ${o.featured ? "bg-gold text-brand-ink hover:bg-brand-gold-hover" : "border border-white/20 text-white hover:border-gold hover:text-gold"}`}>
                      {o.ctaLabel}
                    </a>
                  ) : (
                    <Link to={o.to} data-testid={`offer-cta-${o.slug}`}
                      className="mt-8 text-center px-5 py-4 text-[11px] uppercase tracking-[0.12em] font-semibold transition-colors border border-white/20 text-white hover:border-gold hover:text-gold">
                      {o.ctaLabel}
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* KIT DIGITAL PREMIUM — card d'entrée */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <Reveal>
          <Link
            to="/kit-digital-premium"
            data-testid="home-kit-digital-card"
            className="block border border-[#C9A96E]/30 hover:border-[#C9A96E] transition-colors bg-gradient-to-br from-brand-surface to-brand-ink p-8 md:p-12 group"
          >
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <p className="text-xs uppercase tracking-[0.25em] mb-3 text-[#C9A96E]">📖 Inclus — « Avant que tu ne deviennes »</p>
                <h3 className="font-serif text-3xl md:text-4xl mb-4 text-white leading-tight">
                  Le livre qu'on aurait dû<br />
                  <span className="text-[#C9A96E]">te remettre à ta naissance.</span>
                </h3>
                <p className="text-white/70 font-light leading-relaxed mb-4">
                  Un décodeur de ton identité profonde : pourquoi tu répètes certains schémas,
                  comment tes cycles façonnent tes décisions, et le verrou de lignée qui sabote
                  silencieusement ta réussite.
                </p>
                <p className="text-white/50 font-light leading-relaxed italic text-sm">
                  Pour ceux qui ressentent : « Je réussis, mais je ne me reconnais pas
                  dans ce que je construis. »
                </p>
                <p className="text-white/40 text-xs mt-5 uppercase tracking-[0.2em]">
                  Kit Digital Premium — Triple Fusion™ · + 5 outils exclusifs
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <div className="font-serif text-5xl text-[#C9A96E] mb-3">67 €</div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#C9A96E] border border-[#C9A96E]/40 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E]/10 px-5 py-3 transition-colors">
                  Découvrir le Kit <ArrowUpRight size={14} />
                </div>
              </div>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* FOUNDER */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <img
              src={FOUNDER_IMG}
              alt="Rodrigue CASSILDÉ, Architecte de Vie — Fondateur AETHERIS"
              data-testid="home-founder-photo"
              className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl shadow-black/40 object-cover aspect-[3/4] border border-[#C9A96E]/30"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-luxe text-gold mb-6">Le Fondateur</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Rodrigue Cassildé
            </h2>
            <p className="text-white/60 font-light leading-relaxed text-lg mb-6">
              Autorité de référence en Architecture de Vie, Numérologie Vibratoire et Feng Shui
              Triple Fusion™. Depuis quinze ans, il accompagne dirigeants, entrepreneurs et
              bâtisseurs d'empire vers leur Souveraineté Totale — corps, mental, espace et lignée.
            </p>
            <Link
              to="/a-propos"
              data-testid="founder-cta"
              className="inline-flex items-center gap-2 text-gold hover:text-brand-gold-hover uppercase tracking-[0.15em] text-sm"
            >
              Découvrir son parcours <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 bg-brand-surface border-t border-white/10 py-24 md:py-32">
        <Reveal className="max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight mb-8">
            Votre empire commence par un diagnostic.
          </h2>
          <p className="text-white/60 font-light text-lg mb-10">
            Accordez-vous une première lecture de votre matrice de Souveraineté, sans engagement.
          </p>
          <Link
            to="/diagnostic-inaugural"
            data-testid="final-cta-diagnostic"
            className="inline-flex items-center gap-2 bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-10 py-5 text-sm uppercase tracking-[0.15em] font-semibold"
          >
            Obtenir mon Diagnostic Inaugural — Gratuit <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
